import React, { useState, useEffect } from 'react';
import { compareByDueDate } from '../utils/sortTasks';
import { update, ref } from 'firebase/database';
import InspectorQuickContact from '../components/InspectorQuickContact';
import PersonPeekPanel, { PersonName } from '../components/personPeek/PersonPeekPanel';
import { DealTag } from '../components/DealColorPicker';
import StagingQuickContact from '../components/StagingQuickContact';
import FeatureCardsFlow from '../components/FeatureCardsFlow';
import MaterialApprovalFlow from '../components/MaterialApprovalFlow';
import FeatureUpgradeFlow from '../components/FeatureUpgradeFlow';
import SocialGraphicsFlow from '../components/SocialGraphicsFlow';
import HomeSaleContactQuickContact from '../components/HomeSaleContactQuickContact';
import ClientQuickContact from '../components/ClientQuickContact';
import MatterportClientText, { MatterportScheduleAction } from '../components/MatterportClientText';
import TitleInitiationFlow from '../components/TitleInitiationFlow';
import LenderQuickContact, { lenderFromDeal } from '../components/LenderQuickContact';
import OtherAgentQuickText, { otherAgentOf } from '../components/OtherAgentQuickText';
import HomeSaleAgentQuickContact, { homeSaleAgentOf } from '../components/HomeSaleAgentQuickContact';
import SellerQuestionnaireQuickContact from '../components/SellerQuestionnaireQuickContact';
import ClientBirthdayQuickEntry from '../components/ClientBirthdayQuickEntry';
import GoogleReviewQuickText from '../components/GoogleReviewQuickText';
import FollowUpTextQuickEntry from '../components/FollowUpTextQuickEntry';
import PipelineConfirmQuickText from '../components/PipelineConfirmQuickText';
import PhotographerQuickText from '../components/PhotographerQuickText';
import PhotographyClientText from '../components/PhotographyClientText';
import EmdQuickText from '../components/EmdQuickText';
import LeadTaskQuickActions, { contactForTask } from '../components/LeadTaskQuickActions';
import { useVisibleContacts } from './crm/useCrm';
import { usePipelineRows } from './crm/ContactExtras';
import { database } from '../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTasks, updateTask, deleteTask } from '../services/taskService';
import { isMyTask, isRoleMine, isClientEmailTask, hiddenFromLimited } from '../services/taskRules';
import { getTransaction, getTransactions, getTransactionForUser } from '../services/transactionService';
import { syncActiveDealsToList, loadTeamTransactionTasks, purgeRetiredTasks, migrateEmdTexts, migrateTaskRoles, removeDuplicateUtilitiesTask, removeHomeSaleTasksWhereNone, removeWireFraudForAgent, syncResaleDealsToList, updateSavedTaskLists, removeDuplicateTasks, dedupeRenamedTasks } from '../services/transactionTasks';
import { syncOwnTransactionIndex } from '../services/transactionService';
import { ArrowLeft, Trash2, CheckCircle2, Clock, AlertCircle, Filter } from 'lucide-react';
import { getLogoBooleanTheme } from '../utils/themeUtils';
import { celebrateTaskDone } from '../utils/celebrateTask';
import { showsLeadTasks, canUseQuickActions } from '../config/roleAccess';
import { isOverdueDue, formatDue } from '../utils/dueDates';
import { taskCounts, isOverdueTask, isDueTodayTask, isUpcomingTask, inNext7View, isOpenTask } from '../utils/taskCounts';
import TaskKindLabel from '../components/TaskKindLabel';
import { taskKindInfo } from '../utils/taskKind';

export default function Tasks() {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  // Limited Agent: transaction tasks only (no lead, routine or personal tasks) and no quick actions.
  const leadTasksOn = showsLeadTasks(userRole);
  const quickOn = canUseQuickActions(userRole);
  // Contacts and pipeline rows for the Call / Text / Email buttons on lead tasks.
  const leadContacts = useVisibleContacts({ includeArchived: true });
  const { sellers: pipeSellers, buyers: pipeBuyers } = usePipelineRows();
  const isDarkTheme = true;
  const logoSrc = getLogoBooleanTheme(isDarkTheme);

  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, completed
  const [filterPriority, setFilterPriority] = useState('all'); // all, critical, high, medium, low
  const [filterCategory, setFilterCategory] = useState('all'); // all, transaction, lead
  const [dealInfo, setDealInfo] = useState({}); // transactionId -> client, listing agent, owner, address
  const [filterView, setFilterView] = useState('next7'); // next7, all, overdue, upcoming

  const [transactionNames, setTransactionNames] = useState({});
  const [peek, setPeek] = useState(null);
  const closePeek = React.useCallback(() => setPeek(null), []);

  // Load tasks on component mount
  useEffect(() => {
    loadTasks();
  }, [user, userRole]);

  // Apply filters whenever filter state changes
  useEffect(() => {
    applyFilters();
  }, [tasks, filterStatus, filterPriority, filterCategory, filterView]);

  // Housekeeping (index sync + one-time migrations) runs in the background once per browser session,
  // after the list is on screen, so it never holds up 'Loading tasks...'.
  const runHousekeeping = async () => {
    const key = `tasksHousekeeping_${user?.uid}`;
    if (!user || sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    try {
      const myTxs = (await getTransactions(user.uid)) || [];
      await syncOwnTransactionIndex(user.uid, myTxs);
    } catch (err) {
      console.error('Could not sync the team index', err);
    }
    // Transaction tasks for this user's deals, shown by role (All tasks show to everyone on the deal).
    // One-time: remove tasks that were replaced by a newer version.
    try {
      if (!localStorage.getItem('retiredTasksPurged_v1')) {
        await purgeRetiredTasks();
        localStorage.setItem('retiredTasksPurged_v1', '1');
      }
    } catch (err) {
      console.error('Could not remove retired tasks', err);
    }
    try {
      if (!localStorage.getItem('emdTextsMigrated_v1')) {
        await migrateEmdTexts();
        localStorage.setItem('emdTextsMigrated_v1', '1');
      }
    } catch (err) {
      console.error('Could not update EMD tasks', err);
    }
    try {
      if (!localStorage.getItem('taskRolesMigrated_v3')) {
        await migrateTaskRoles();
        localStorage.setItem('taskRolesMigrated_v3', '1');
      }
    } catch (err) {
      console.error('Could not update task roles', err);
    }
    try {
      if (!localStorage.getItem('utilDuplicateRemoved_v1')) {
        await removeDuplicateUtilitiesTask();
        localStorage.setItem('utilDuplicateRemoved_v1', '1');
      }
    } catch (err) {
      console.error('Could not remove duplicate utilities task', err);
    }
    try {
      if (!localStorage.getItem('homeSaleNoneCleanup_v2')) {
        await removeHomeSaleTasksWhereNone();
        localStorage.setItem('homeSaleNoneCleanup_v2', '1');
      }
    } catch (err) {
      console.error('Could not remove home sale tasks', err);
    }
    try {
      if (!localStorage.getItem('wireFraudAgentRemoved_v1')) {
        await removeWireFraudForAgent();
        localStorage.setItem('wireFraudAgentRemoved_v1', '1');
      }
    } catch (err) {
      console.error('Could not remove wire fraud task', err);
    }
    try {
      if (!localStorage.getItem('duplicateTasksRemoved_v2')) {
        await removeDuplicateTasks();
        localStorage.setItem('duplicateTasksRemoved_v2', '1');
      }
    } catch (err) {
      console.error('Could not remove duplicate tasks', err);
    }
    try {
      if (!localStorage.getItem('savedListsUpdated_v4')) {
        await updateSavedTaskLists();
        localStorage.setItem('savedListsUpdated_v4', '1');
      }
    } catch (err) {
      console.error('Could not update saved task lists', err);
    }
    try {
      if (!localStorage.getItem('renamedTasksDeduped_v2')) {
        await dedupeRenamedTasks();
        localStorage.setItem('renamedTasksDeduped_v2', '1');
      }
    } catch (err) {
      console.error('Could not remove duplicate renamed tasks', err);
    }
    try {
      if (!localStorage.getItem('resaleListSync_v5')) {
        await syncResaleDealsToList();
        localStorage.setItem('resaleListSync_v5', '1');
      }
    } catch (err) {
      console.error('Could not sync task lists to deals', err);
    }
    try {
      // One-time: adds any new construction list task that open new construction deals are missing.
      if (!localStorage.getItem('ncListSync_v1')) {
        await syncActiveDealsToList('new-construction-buyer-under-contract');
        localStorage.setItem('ncListSync_v1', '1');
      }
    } catch (err) {
      console.error('Could not sync new construction tasks to deals', err);
    }
    return true;
  };

  const loadTasks = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError(null);

      // ?sample=1 shows built-in example tasks without touching the database.
      if (new URLSearchParams(window.location.search).get('sample') === '1') {
        const off = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };
        const sample = [
          { id: 's1', title: 'Send inspection report to buyer', description: 'Email the report and summary to the buyer and TC.', dueDate: off(-2), status: 'pending', priority: 'critical', transactionId: 'sample-maria' },
          { id: 's2', title: 'Confirm earnest money received', description: 'Check with title company that the EMD cleared.', dueDate: off(0), status: 'pending', priority: 'high', transactionId: 'sample-maria' },
          { id: 's3', title: 'Order appraisal', description: 'Coordinate appraisal date with lender.', dueDate: off(1), status: 'in-progress', priority: 'high', transactionId: 'sample-james' },
          { id: 's4', title: 'Schedule final walk-through', description: 'Confirm time with buyer and listing agent.', dueDate: off(5), status: 'pending', priority: 'medium', transactionId: 'sample-james' },
          { id: 's5', title: 'Send closing disclosure reminder', description: 'Remind buyer to review the CD three days before closing.', dueDate: off(9), status: 'pending', priority: 'medium', transactionId: 'sample-linda' },
          { id: 's6', title: 'Upload signed listing agreement', description: 'Add signed agreement to the transaction file.', dueDate: off(-7), status: 'completed', priority: 'low', transactionId: 'sample-robert' },
        ];
        const todayIso = off(0);
        setTasks(sample);
        setTransactionNames({
          'sample-maria': 'Maria Alvarez',
          'sample-james': 'James Whitfield',
          'sample-linda': 'Linda Okafor',
          'sample-robert': 'Robert Chen',
        });
        return;
      }

      if (!user) throw new Error('User not authenticated');

      let allTasks = await getTasks(user.uid);

      const txRows = await loadTeamTransactionTasks(user.uid, userRole);
      allTasks = [...allTasks, ...txRows];
      // Automatic emails never show on the task list. Older rows may lack the auto flag, so the name is checked too.
      const isAutoEmail = (t) => !isClientEmailTask(t) && (t.mode === 'auto' || t.automatic === true || /^auto email/i.test(t.title || t.name || ''));
      setTasks([...allTasks.filter(t => !isAutoEmail(t) && !hiddenFromLimited(t, userRole) && (isMyTask(t, userRole) || t.source === 'transaction') && (leadTasksOn || t.source === 'transaction'))]);

      // Deal details for the quick action buttons on transaction tasks.
      // Every transaction task on the list, not only the team rows, so a deal task saved on the personal list still gets its buttons.
      const txIds = [...new Set([...txRows, ...allTasks.filter(t => t.source === 'transaction')].map(t => t.transactionId).filter(Boolean))];
      const infoEntries = await Promise.all(txIds.map(async (txId) => {
        try {
          const tx = await getTransactionForUser(user.uid, txId, userRole);
          if (!tx) return [txId, null];
          return [txId, {
            ownerUid: tx.ownerUid || user.uid,
            dealAddress: tx.propertyAddress || '',
            client: { firstName: tx.firstName, lastName: tx.lastName, email: tx.email, phone: tx.phone },
            listingAgent: { firstName: tx.homeSaleAgentFirstName, lastName: tx.homeSaleAgentLastName, email: tx.homeSaleAgentEmail, phone: tx.homeSaleAgentPhone },
            otherAgent: otherAgentOf(tx),
            lender: tx.lender || null,
            deal: { stager: tx.stager || '', propertyAddress: tx.propertyAddress || '', photographyDate: tx.photographyDate || '' },
            full: tx,
          }];
        } catch (err) {
          console.error('Could not load deal for task actions', err);
          return [txId, null];
        }
      }));
      setDealInfo(Object.fromEntries(infoEntries));

      // Transaction names: reuse the deals loaded above, and fetch any others in parallel.
      const txNames = {};
      infoEntries.forEach(([id, info]) => { if (info?.full) txNames[id] = `${info.full.firstName || 'Unknown'} ${info.full.lastName || ''}`; });
      const missing = [...new Set(allTasks.map(t => t.transactionId).filter(id => id && !txNames[id]))];
      await Promise.all(missing.map(async (id) => {
        try {
          const tx = await getTransactionForUser(user.uid, id, userRole);
          if (tx) txNames[id] = `${tx.firstName || 'Unknown'} ${tx.lastName || ''}`;
        } catch (err) {
          console.error('Error fetching transaction name:', err);
        }
      }));
      setTransactionNames(txNames);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
    // After the list shows, tidy up in the background; reload quietly if anything ran.
    if (!quiet) runHousekeeping().then((ran) => { if (ran) loadTasks({ quiet: true }); }).catch(() => {});
  };

  const applyFilters = () => {
    // Done and N/A tasks come off the list.
    let result = [...tasks].filter(isOpenTask);

    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(t => t.status === filterStatus);
    }

    // Transaction tasks come from a deal; lead tasks are everything else on the list.
    if (filterCategory === 'transaction') {
      result = result.filter(t => t.source === 'transaction');
    } else if (filterCategory === 'lead') {
      result = result.filter(t => t.source !== 'transaction');
    }

    // Apply priority filter
    if (filterPriority !== 'all') {
      result = result.filter(t => t.priority === filterPriority);
    }

    // Apply view filter
    if (filterView === 'next7') {
      // Tasks due in the next 7 days, plus anything still open from before today.
      result = result.filter(t => inNext7View(t));
    } else if (filterView === 'today') {
      result = result.filter(t => isDueTodayTask(t));
    } else if (filterView === 'overdue') {
      result = result.filter(t => isOverdueTask(t));
    } else if (filterView === 'upcoming') {
      result = result.filter(t => isUpcomingTask(t));
    }

    // Sort by due date
    // Earliest due date first, then priority (critical to low). Tasks with no date go last.
    const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
    const dateKey = (t) => (t.dueDate ? new Date(`${t.dueDate}`.slice(0, 10) + 'T00:00:00').getTime() : Number.POSITIVE_INFINITY);
    const rank = (t) => (PRIORITY_RANK[t.priority] ?? 4);
    result.sort((a, b) => compareByDueDate(a, b) || (rank(a) - rank(b)));

    setFilteredTasks(result);
  };

  const handleQuickStatus = async (task, newStatus, node = null) => {
    try {
      if (task.source === 'transaction') {
        const done = newStatus === 'completed';
        await update(ref(database, `transactions/${task.transactionId}/tasks/${task.id}`), {
          status: newStatus,
          completedBy: done ? (user?.displayName || user?.email || 'Unknown') : null,
          completedAt: done ? new Date().toISOString() : null,
        });
      } else {
        await updateTask(user.uid, task.id, { status: newStatus });
      }
      if (newStatus === 'completed' && node) celebrateTaskDone(node, loadTasks);
      else await loadTasks();
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(user.uid, taskId, { status: newStatus });
      await loadTasks();
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await deleteTask(user.uid, taskId);
      await loadTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task');
    }
  };

  const handleAddNote = async (taskId, currentNote) => {
    const newNote = prompt('Add or edit note:', currentNote || '');
    if (newNote !== null) {
      try {
        await updateTask(user.uid, taskId, { notes: newNote });
        await loadTasks();
      } catch (err) {
        console.error('Error updating task note:', err);
        setError('Failed to update task note');
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return '#FF6B6B';
      case 'high':
        return '#FFA500';
      case 'medium':
        return '#FFD700';
      case 'low':
        return '#4CAF50';
      default:
        return '#999999';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={18} style={{ color: '#4CAF50' }} />;
      case 'in-progress':
        return <Clock size={18} style={{ color: '#FFA500' }} />;
      default:
        return <AlertCircle size={18} style={{ color: '#999999' }} />;
    }
  };

  const formatDate = (dateStr) => formatDue(dateStr);

  const isOverdue = (dueDate, status) => isOverdueDue(dueDate, status);

  // Stat cards count the same open tasks the list shows (Done and N/A are off the list), Eastern days.
  const taskStats = taskCounts(tasks);
  const statDefs = [
    ['Overdue', taskStats.overdue, '#D64545', 'overdue'],
    ['Due today', taskStats.dueToday, '#1a1a1a', 'today'],
    ['Next 7 days', taskStats.upcoming, '#8C6D1F', 'upcoming'],
  ];

  return (
    <div style={styles.container}>
      <PersonPeekPanel person={peek} onClose={closePeek} />
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Task Management</h1>
      </div>

      {/* Stats Bar */}
      <div style={styles.statsBar}>
        {statDefs.map(([label, n, color, view]) => (
          <button type="button" key={label} onClick={() => setFilterView(view)} style={styles.statCard} aria-label={`${label}: ${n}. Show these tasks`}>
            <div style={{ ...styles.statNumber, color }}>{n}</div>
            <div style={styles.statLabel}>{label}</div>
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Filters */}
        <div style={styles.filterSection}>
          <div style={styles.filterHeader}>
            <Filter size={20} />
            <h3 style={styles.filterTitle}>Filters</h3>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>View</label>
            <select
              value={filterView}
              onChange={(e) => setFilterView(e.target.value)}
              style={styles.select}
            >
              <option value="next7">Next 7 Days + Overdue</option>
              <option value="all">All Tasks</option>
              <option value="overdue">Overdue Only</option>
              <option value="today">Due Today</option>
              <option value="upcoming">Next 7 Days (tomorrow on, not overdue)</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Task type</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Tasks</option>
              <option value="transaction">Transaction Tasks</option>
              {leadTasksOn && <option value="lead">Lead Tasks</option>}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Statuses</option>
              <option value="pending">To do</option>
            </select>
          </div>

          </div>

        {/* Tasks List */}
        <div style={styles.tasksSection}>
          {error && (
            <div style={styles.errorBox}>
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          {loading ? (
            <div style={styles.loadingBox}>
              <p style={styles.loadingText}>Loading tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div style={styles.emptyBox}>
              <p style={styles.emptyText}>No tasks match your filters</p>
            </div>
          ) : (
            <div style={styles.tasksList}>
              {filteredTasks.map((task) => (
                <div
                  key={task.id}
                  data-task-card="true"
                  style={{
                    ...styles.taskCard,
                    borderLeftColor: isOverdue(task.dueDate, task.status) ? '#D64545' : (dealInfo[task.transactionId]?.full?.dealColor || '#B8962E'),
                    backgroundColor: isOverdue(task.dueDate, task.status)
                      ? '#FDE3E3'
                      : '#ffffff'
                  }}
                >
                  {/* Task Header */}
                  <div style={styles.taskHeader}>
                    <div style={styles.taskTitleSection}>
                      <div style={styles.taskStatusIcon}>
                        {getStatusIcon(task.status)}
                      </div>
                      <div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
  <DealTag color={dealInfo[task.transactionId]?.full?.dealColor} address={dealInfo[task.transactionId]?.dealAddress} onClick={task.transactionId ? () => navigate(`/transactions/${task.transactionId}`) : undefined} />
                          <TaskKindLabel task={task} />
                        </div>
                        <h4 style={styles.taskTitle}>{task.title}</h4>
                        <p style={styles.taskDescription}>{task.description}</p>
                        {transactionNames[task.transactionId] && (
                          <p style={styles.transactionName}>
                            Transaction: <PersonName onOpen={() => setPeek({ transaction: { id: task.transactionId, ...(dealInfo[task.transactionId]?.full || {}) }, name: transactionNames[task.transactionId] })}>{transactionNames[task.transactionId]}</PersonName>
                          </p>
                        )}
                      </div>
                    </div>
                    <div style={{ ...styles.taskActions, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={(e) => handleQuickStatus(task, 'completed', e.currentTarget)} style={quickBtn}>Done</button>
                      <button type="button" onClick={() => handleQuickStatus(task, 'na')} style={quickBtnNA}>N/A</button>
                    </div>
                      <button
                        onClick={() => handleDeleteTask(task.id)}
                        style={styles.deleteButton}
                        title="Delete task"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Task Details */}
                  <div style={styles.taskDetails}>
                    <div style={styles.taskDetail}>
                      <span style={styles.detailLabel}>Due:</span>
                      <span
                        style={{
                          ...styles.detailValue,
                          color: isOverdue(task.dueDate, task.status)
                            ? '#B42323'
                            : '#1A1A1A',
                          fontWeight: isOverdue(task.dueDate, task.status) ? 700 : undefined
                        }}
                      >
                        {formatDate(task.dueDate)}{isOverdue(task.dueDate, task.status) ? ' (OVERDUE)' : ''}
                      </span>
                    </div>
                    <div style={styles.taskDetail}>
                      <span style={styles.detailLabel}>Task type:</span>
                      <span style={styles.detailValue}>
                        {taskKindInfo(task).label}
                      </span>
                    </div>
                    {task.crmContactId && (
                      <div style={styles.taskDetail}>
                        <span style={styles.detailLabel}>Lead:</span>
                        <PersonName style={styles.detailValue} onOpen={() => setPeek({ contactId: task.crmContactId, name: task.crmContactName })}>{task.crmContactName || 'View lead'}</PersonName>
                      </div>
                    )}
                    {!task.crmContactId && (task.sellerInterestId || task.buyerInterestId) && (
                      <div style={styles.taskDetail}>
                        <span style={styles.detailLabel}>{task.buyerInterestId ? 'Buyer:' : 'Seller:'}</span>
                        <PersonName style={styles.detailValue} onOpen={() => setPeek({ sellerInterestId: task.sellerInterestId, buyerInterestId: task.buyerInterestId, name: task.crmContactName })}>{task.crmContactName || 'View person'}</PersonName>
                      </div>
                    )}
                    {task.source === 'crm-lead-task' && task.notes && (
                      <div style={styles.taskDetail}><span style={styles.detailLabel}>Notes:</span><span style={{ ...styles.detailValue, whiteSpace: 'pre-wrap' }}>{task.notes}</span></div>
                    )}
                    <div style={styles.taskDetail}>
                      <span style={styles.detailLabel}>Status:</span>
                      <span style={styles.detailValue}>
                        {task.status === 'completed' ? 'Done' : task.status === 'na' ? 'N/A' : 'To do'}
                      </span>
                    </div>
                  </div>

                  {quickOn && <PipelineConfirmQuickText task={task} onSent={() => handleQuickStatus(task, 'completed')} />}
                  {quickOn && leadTasksOn && (task.crmContactId || task.sellerInterestId || task.buyerInterestId) && (
                    <LeadTaskQuickActions task={task} contact={contactForTask(task, leadContacts, pipeSellers, pipeBuyers)} uid={user?.uid} isAdmin={userRole === 'Admin'} senderName={user?.displayName || ''} onCleared={() => loadTasks({ quiet: true })} />
                  )}
                  {quickOn && <InspectorQuickContact task={task} dealAddress={dealInfo[task.transactionId]?.dealAddress || ''} client={dealInfo[task.transactionId]?.client} />}
                  {task.source === 'transaction' && dealInfo[task.transactionId] && (() => {
                    const d = dealInfo[task.transactionId];
                    const done = () => handleQuickStatus(task, 'completed');
                    return (
                      <>
                        {quickOn && (<>
                          <HomeSaleContactQuickContact task={task} listingAgent={d.listingAgent} client={d.client} onSent={done} />
                          <ClientQuickContact task={task} client={d.client} onSent={done} />
                          <MatterportScheduleAction task={task} transactionId={task.transactionId} ownerUid={d.ownerUid} onSaved={loadTasks} />
                          <MatterportClientText task={task} client={d.client} ownerUid={d.ownerUid} deal={d.full} />
                          <TitleInitiationFlow task={task} deal={d.full} ownerUid={d.ownerUid} transactionId={task.transactionId} onSent={done} />
                          <LenderQuickContact task={task} lender={lenderFromDeal(d.full)} dealAddress={d.dealAddress} onSent={done} />
                          <OtherAgentQuickText task={task} otherAgent={d.otherAgent} dealAddress={d.dealAddress} senderName={user?.displayName || ''} onSent={done} />
                          <HomeSaleAgentQuickContact task={task} agent={homeSaleAgentOf(d.full)} contingencyDate={d.full?.underContract?.homeSaleContingencyDate} onSent={done} />
                          <SellerQuestionnaireQuickContact task={task} deal={d.full} onSent={done} />
                          <ClientBirthdayQuickEntry task={task} deal={d.full} ownerUid={d.ownerUid} transactionId={task.transactionId} onSent={done} />
                          <GoogleReviewQuickText task={task} deal={d.full} ownerUid={d.ownerUid} onSent={done} />
                          <FollowUpTextQuickEntry task={task} deal={d.full} onSent={done} />
                          <StagingQuickContact task={task} deal={d.deal} ownerUid={d.ownerUid} />
                          <PhotographerQuickText task={task} deal={d.full} ownerUid={d.ownerUid} />
                          <PhotographyClientText task={task} deal={d.full} client={d.client} ownerUid={d.ownerUid} />
                          {!/^(First|Second) EMD Due|^Earnest Money Due Today|^Text Client/i.test(task.name || task.title || '') && (
                            <EmdQuickText task={task} ownerUid={d.ownerUid} clientName={`${d.client.firstName || ''} ${d.client.lastName || ''}`.trim()} dealAddress={d.dealAddress} onSent={done} />
                          )}
                        </>)}
                        <FeatureCardsFlow task={task} txId={task.transactionId} />
                        <MaterialApprovalFlow task={task} txId={task.transactionId} onChanged={loadTasks} />
                        <FeatureUpgradeFlow task={task} txId={task.transactionId} />
                        <SocialGraphicsFlow task={task} txId={task.transactionId} />
                      </>
                    );
                  })()}

                  {/* Notes Section */}
                  {task.notes && (
                    <div style={styles.notesSection}>
                      <p style={styles.notesLabel}>Notes:</p>
                      <p style={styles.notesText}>{task.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f2f2f2',
    color: '#000000',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  header: {
    backgroundColor: '#000000',
    borderBottom: '2px solid #D4AF37',
    padding: '20px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  logoButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center'
  },
  logo: {
    height: '40px',
    width: 'auto',
    maxWidth: '150px'
  },
  headerTitle: {
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '0',
    letterSpacing: '2px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  statsBar: {
    backgroundColor: '#000000',
    borderBottom: '1px solid #d8c9a8',
    padding: '12px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '8px'
  },
  statCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '8px',
    padding: '8px 4px',
    textAlign: 'center',
    cursor: 'pointer',
    font: 'inherit',
    minWidth: 0
  },
  statNumber: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#8C6D1F',
    margin: '0 0 4px 0'
  },
  statLabel: {
    fontSize: '12px',
    color: '#999999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0'
  },
  mainContent: {
    padding: '40px',
    display: 'grid',
    gridTemplateColumns: '250px 1fr',
    gap: '40px'
  },
  filterSection: {
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '8px',
    padding: '24px',
    height: 'fit-content',
    position: 'sticky',
    top: '20px'
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  },
  filterTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#8C6D1F',
    margin: '0'
  },
  filterGroup: {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#999999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  select: {
    padding: '10px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '4px',
    color: '#1a1a1a',
    fontSize: '13px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  tasksSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  tasksList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  taskCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderLeft: '4px solid #D4AF37',
    borderRadius: '8px',
    padding: '10px 14px',
    transition: 'all 0.3s ease'
  },
  taskHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '6px'
  },
  taskTitleSection: {
    display: 'flex',
    gap: '12px',
    flex: 1
  },
  taskStatusIcon: {
    marginTop: '4px',
    minWidth: '24px'
  },
  taskTitle: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#1a1a1a'
  },
  taskDescription: {
    fontSize: '13px',
    color: '#999999',
    margin: '0 0 4px 0'
  },
  transactionName: {
    fontSize: '12px',
    color: '#8C6D1F',
    margin: '0',
    fontStyle: 'italic'
  },
  taskActions: {
    display: 'flex',
    gap: '8px'
  },
  deleteButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#FF6B6B',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    transition: 'all 0.3s ease'
  },
  taskDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid #d8c9a8'
  },
  taskDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  detailLabel: {
    fontSize: '11px',
    color: '#666666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  detailValue: {
    fontSize: '14px',
    color: '#1a1a1a',
    fontWeight: '500'
  },
  taskStatusSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '6px'
  },
  statusLabel: {
    fontSize: '12px',
    color: '#999999',
    fontWeight: '600',
    minWidth: '50px'
  },
  statusSelect: {
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '4px',
    color: '#1a1a1a',
    fontSize: '13px',
    flex: 1,
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  notesSection: {
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '4px',
    padding: '12px',
    marginBottom: '6px'
  },
  notesLabel: {
    fontSize: '11px',
    color: '#999999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 8px 0'
  },
  notesText: {
    fontSize: '13px',
    color: '#CCCCCC',
    margin: '0',
    lineHeight: '1.4'
  },
  addNoteButton: {
    padding: '8px 16px',
    backgroundColor: '#e6dcc3',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  errorBox: {
    backgroundColor: '#f5efe1',
    border: '1px solid #D4AF37',
    borderRadius: '4px',
    padding: '12px',
    marginBottom: '20px'
  },
  errorText: {
    color: '#1a1a1a',
    fontSize: '14px',
    margin: '0'
  },
  loadingBox: {
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '8px',
    padding: '40px',
    textAlign: 'center'
  },
  loadingText: {
    color: '#999999',
    fontSize: '14px',
    margin: '0'
  },
  emptyBox: {
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '8px',
    padding: '40px',
    textAlign: 'center'
  },
  emptyText: {
    color: '#999999',
    fontSize: '14px',
    margin: '0'
  }
};

// Quick Done / N/A buttons.
const quickBtn = { padding: '8px 14px', borderRadius: 6, border: '1px solid #3C9D5D', background: '#3C9D5D', color: '#FFFFFF', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const quickBtnNA = { padding: '8px 14px', borderRadius: 6, border: '1px solid #9AA1AD', background: '#FFFFFF', color: '#5B6270', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
